import type { MiniAppSnapshot, MiniAppValues } from "@rakazo/contracts";

type Pending = {
  values: MiniAppValues;
  mutationId: string;
  revision: number;
  waiters: Array<{ resolve: () => void; reject: (error: unknown) => void }>;
};
/** Serializes one app's saves, coalesces rapid edits, and retries an ambiguous save with its original id. */
export class MiniAppSaveQueue {
  private active = false;
  private pending: Pending | undefined;
  private failed: Pending | undefined;
  private revision: number;
  latest: MiniAppValues;
  constructor(
    initial: MiniAppSnapshot,
    private save: (
      values: MiniAppValues,
      revision: number,
      mutationId: string,
    ) => Promise<MiniAppSnapshot>,
    private changed: (status: "saving" | "saved" | "error", error?: unknown) => void,
  ) {
    this.revision = initial.revision;
    this.latest = initial.values;
  }
  enqueue(values: MiniAppValues): Promise<void> {
    this.latest = values;
    const promise = new Promise<void>((resolve, reject) => {
      if (this.pending) {
        this.pending.values = values;
        this.pending.waiters.push({ resolve, reject });
      } else
        this.pending = {
          values,
          revision: this.revision,
          mutationId: crypto.randomUUID(),
          waiters: [{ resolve, reject }],
        };
    });
    if (this.failed) {
      this.pending!.waiters.splice(0).forEach((w) =>
        w.reject(new Error("App data is not saved. Retry the save.")),
      );
    } else void this.drain();
    return promise;
  }
  retry() {
    if (!this.active) void this.drain(true);
  }
  private async drain(retry = false) {
    if (this.active || (this.failed && !retry)) return;
    const item = this.failed ?? this.pending;
    if (!item) return;
    if (item === this.pending) {
      this.pending = undefined;
      item.revision = this.revision;
    }
    this.active = true;
    this.changed("saving");
    try {
      const saved = await this.save(item.values, item.revision, item.mutationId);
      this.revision = saved.revision;
      this.failed = undefined;
      item.waiters.splice(0).forEach((w) => w.resolve());
      this.active = false;
      if (this.pending) void this.drain();
      else this.changed("saved");
    } catch (error) {
      this.failed = item;
      item.waiters.splice(0).forEach((w) => w.reject(error));
      this.pending?.waiters.splice(0).forEach((w) => w.reject(error));
      this.active = false;
      this.changed("error", error);
    }
  }
}
